import { useCallback, useEffect, useRef, useState } from 'react';

import { useColumnManager } from './useColumnManager';

export interface UseDriveColumnsResult {
  columns: ReturnType<typeof useColumnManager>['columns'];
  visibleColumns: ReturnType<typeof useColumnManager>['visibleColumns'];
  toggleColumnVisibility: ReturnType<typeof useColumnManager>['toggleColumnVisibility'];
  reorderColumns: ReturnType<typeof useColumnManager>['reorderColumns'];
  resizeColumn: ReturnType<typeof useColumnManager>['resizeColumn'];
  resetToDefaults: ReturnType<typeof useColumnManager>['resetToDefaults'];
  getColumn: ReturnType<typeof useColumnManager>['getColumn'];
  isDragging: boolean;
  draggedColumnId: string | null;
  dragOverColumnId: string | null;
  handleDragStart: (event: React.DragEvent, columnId: string) => void;
  handleDragOver: (event: React.DragEvent, columnId: string) => void;
  handleDragLeave: () => void;
  handleDrop: (event: React.DragEvent, columnId: string) => void;
  handleDragEnd: () => void;
  resizingColumnId: string | null;
  handleResizeStart: (event: React.MouseEvent, columnId: string) => void;
  isColumnSettingsOpen: boolean;
  setIsColumnSettingsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  columnSettingsMenuPosition: { x: number; y: number } | null;
  setColumnSettingsMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  columnSettingsMenuRef: React.RefObject<HTMLDivElement>;
  columnSettingsButtonRef: React.RefObject<HTMLButtonElement>;
  isColumnSettingsLoaded: boolean;
}

export const useDriveColumns = (): UseDriveColumnsResult => {
  const {
    columns,
    visibleColumns,
    toggleColumnVisibility,
    reorderColumns,
    resizeColumn,
    resetToDefaults,
    getColumn,
  } = useColumnManager();

  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [columnSettingsMenuPosition, setColumnSettingsMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const columnSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const columnSettingsMenuRef = useRef<HTMLDivElement>(null);
  const isColumnSettingsLoaded = columnSettingsMenuRef.current !== null;

  const handleDragStart = useCallback((event: React.DragEvent, columnId: string) => {
    setDraggedColumnId(columnId);
    setIsDragging(true);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent, columnId: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (draggedColumnId && draggedColumnId !== columnId) {
      setDragOverColumnId(columnId);
    }
  }, [draggedColumnId]);

  const handleDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent, targetColumnId: string) => {
    event.preventDefault();
    if (draggedColumnId && draggedColumnId !== targetColumnId) {
      const targetColumn = getColumn(targetColumnId);
      if (targetColumn) {
        reorderColumns(draggedColumnId, targetColumn.order);
      }
    }
    setDraggedColumnId(null);
    setDragOverColumnId(null);
    setIsDragging(false);
  }, [draggedColumnId, getColumn, reorderColumns]);

  const handleDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (resizingColumnId) {
      const handleMove = (event: MouseEvent) => {
        const deltaX = event.clientX - resizeStartX;
        const newWidth = resizeStartWidth + deltaX;
        resizeColumn(resizingColumnId, newWidth);
      };

      const handleUp = () => {
        setResizingColumnId(null);
        setResizeStartX(0);
        setResizeStartWidth(0);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
    return undefined;
  }, [resizingColumnId, resizeColumn, resizeStartWidth, resizeStartX]);

  const handleResizeStart = useCallback((event: React.MouseEvent, columnId: string) => {
    event.stopPropagation();
    setResizingColumnId(columnId);
    setResizeStartX(event.clientX);
    const column = getColumn(columnId);
    if (column) {
      setResizeStartWidth(column.defaultWidth);
    }
  }, [getColumn]);

  return {
    columns,
    visibleColumns,
    toggleColumnVisibility,
    reorderColumns,
    resizeColumn,
    resetToDefaults,
    getColumn,
    isDragging,
    draggedColumnId,
    dragOverColumnId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resizingColumnId,
    handleResizeStart,
    isColumnSettingsOpen,
    setIsColumnSettingsOpen,
    columnSettingsMenuPosition,
    setColumnSettingsMenuPosition,
    columnSettingsMenuRef,
    columnSettingsButtonRef,
    isColumnSettingsLoaded,
  };
};

